import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Lock, DollarSign, Languages, Shield, ShieldCheck, ShieldOff, Smartphone, ExternalLink } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';
import { useCurrency } from '@/hooks/useCurrency';
import { useMFA } from '@/hooks/useMFA';
import { useMFAPolicy } from '@/hooks/useMFAPolicy';
import { TrustedDevicesManager } from '@/components/TrustedDevicesManager';
import { useNavigate } from 'react-router-dom';

// Note: Les messages de validation sont en français par défaut
// TODO: Créer des fonctions de validation dynamiques avec traductions
const profileSchema = z.object({
  full_name: z.string().min(2, 'Le nom doit contenir au moins 2 caractères'),
  phone: z.string().optional(),
  business_name: z.string().optional(),
});

const emailSchema = z.object({
  newEmail: z.string().email('Adresse email invalide'),
});

const passwordSchema = z.object({
  newPassword: z.string().min(6, 'Le mot de passe doit contenir au moins 6 caractères'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
});

const AccountSettings = () => {
  const { user, profile, updateProfile, updateEmail, updatePassword } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { displayCurrency, setDisplayCurrency } = useCurrency();
  const { listFactors, unenroll } = useMFA();
  const { requiresMFA } = useMFAPolicy();
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState<string | null>(null);

  // MFA state
  const [mfaFactors, setMfaFactors] = useState<any[]>([]);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [unenrolling, setUnenrolling] = useState(false);

  useEffect(() => {
    listFactors().then(({ data }) => {
      setMfaFactors(data?.totp?.filter((f: any) => f.status === 'verified') ?? []);
      setMfaLoading(false);
    });
  }, []);

  const handleUnenroll = async (factorId: string) => {
    if (!confirm('Désactiver le MFA ? Vous devrez reconfigurer une application authenticator si le MFA est requis.')) return;
    setUnenrolling(true);
    await unenroll(factorId);
    setMfaFactors([]);
    setUnenrolling(false);
  };

  const profileForm = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
      business_name: profile?.business_name || '',
    },
  });

  const emailForm = useForm({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      newEmail: '',
    },
  });

  const passwordForm = useForm({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onUpdateProfile = async (data: z.infer<typeof profileSchema>) => {
    const { error } = await updateProfile({
      full_name: data.full_name,
      phone: data.phone || null,
      business_name: data.business_name || null,
    });
    
    if (!error) {
      setActiveForm(null);
    }
  };

  const onUpdateEmail = async (data: z.infer<typeof emailSchema>) => {
    const { error } = await updateEmail(data.newEmail);
    
    if (!error) {
      setActiveForm(null);
      emailForm.reset();
    }
  };

  const onUpdatePassword = async (data: z.infer<typeof passwordSchema>) => {
    const { error } = await updatePassword(data.newPassword);
    
    if (!error) {
      setActiveForm(null);
      passwordForm.reset();
    }
  };

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage as 'fr' | 'en' | 'lg-fr');
  };

  return (
    <div className="space-y-3">
      {/* Profile Information - Amazon Style */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4 gap-2">
            <div className="flex items-center space-x-2 md:space-x-3 flex-1 min-w-0">
              <div className="p-1.5 md:p-2 bg-orange-50 rounded-lg flex-shrink-0">
                <User className="h-4 w-4 md:h-5 md:w-5 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm md:text-base truncate">{t('accountSettings.personalInfo')}</h3>
                <p className="text-xs md:text-sm text-gray-500 truncate">{t('accountSettings.manageProfile')}</p>
              </div>
            </div>
            {activeForm !== 'profile' && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setActiveForm('profile')}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs md:text-sm flex-shrink-0 h-8 px-2"
              >
                {t('accountSettings.editInfo')}
              </Button>
            )}
          </div>

          {activeForm !== 'profile' ? (
            <div className="space-y-3 pl-0 md:pl-11">
              <div className="flex flex-col md:flex-row md:justify-between py-2 border-b border-gray-100 gap-1">
                <span className="text-xs md:text-sm font-medium text-gray-600">{t('accountSettings.fullName')}</span>
                <span className="text-xs md:text-sm text-gray-900 truncate">
                  {profile?.full_name || t('accountSettings.notProvided')}
                </span>
              </div>
              <div className="flex flex-col md:flex-row md:justify-between py-2 border-b border-gray-100 gap-1">
                <span className="text-xs md:text-sm font-medium text-gray-600">{t('accountSettings.phone')}</span>
                <span className="text-xs md:text-sm text-gray-900 truncate">
                  {profile?.phone || t('accountSettings.notProvided')}
                </span>
              </div>
              {(profile?.vendor_status === 'approved' || profile?.system_role === 'admin' || profile?.system_role === 'super_admin') && (
                <div className="flex flex-col md:flex-row md:justify-between py-2 gap-1">
                  <span className="text-xs md:text-sm font-medium text-gray-600">{t('accountSettings.businessName')}</span>
                  <span className="text-xs md:text-sm text-gray-900 truncate">
                    {profile?.business_name || t('accountSettings.notProvided')}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={profileForm.handleSubmit(onUpdateProfile)} className="space-y-4 pl-0 md:pl-11">
              <div className="space-y-3">
                <div>
                  <Label htmlFor="full_name" className="text-sm font-medium">
                    {t('accountSettings.fullName')} *
                  </Label>
                  <Input
                    id="full_name"
                    {...profileForm.register('full_name')}
                    className="mt-1.5"
                  />
                  {profileForm.formState.errors.full_name && (
                    <p className="text-sm text-red-600 mt-1">
                      {profileForm.formState.errors.full_name.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium">
                    {t('accountSettings.phone')}
                  </Label>
                  <Input
                    id="phone"
                    {...profileForm.register('phone')}
                    className="mt-1.5"
                  />
                </div>
                {(profile?.vendor_status === 'approved' || profile?.system_role === 'admin' || profile?.system_role === 'super_admin') && (
                  <div>
                    <Label htmlFor="business_name" className="text-sm font-medium">
                      {t('accountSettings.businessName')}
                    </Label>
                    <Input
                      id="business_name"
                      {...profileForm.register('business_name')}
                      className="mt-1.5"
                      placeholder={t('accountSettings.businessNamePlaceholder')}
                    />
                  </div>
                )}
              </div>
              <div className="flex space-x-2 pt-2">
                <Button 
                  type="submit" 
                  disabled={profileForm.formState.isSubmitting}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {profileForm.formState.isSubmitting ? t('accountSettings.saving') : t('accountSettings.save')}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setActiveForm(null)}
                >
                  {t('accountSettings.cancel')}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Login & Security Section */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 md:space-x-3 mb-4">
            <div className="p-1.5 md:p-2 bg-blue-50 rounded-lg flex-shrink-0">
              <Lock className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm md:text-base truncate">Login & Security</h3>
              <p className="text-xs md:text-sm text-gray-500 truncate">Manage your email and password</p>
            </div>
          </div>

          {/* Email Section */}
          <div className="space-y-4 pl-0 md:pl-11">
            <div>
              <div className="flex items-start justify-between mb-2 gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <Mail className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-xs md:text-sm font-medium text-gray-700">{t('accountSettings.emailAddress')}</span>
                  </div>
                  {activeForm !== 'email' && (
                    <p className="text-xs md:text-sm text-gray-900 ml-5 md:ml-6 truncate">{user?.email}</p>
                  )}
                </div>
                {activeForm !== 'email' && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setActiveForm('email')}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 -mt-1 text-[10px] md:text-xs flex-shrink-0 h-7 px-2"
                  >
                    <span className="hidden md:inline">{t('accountSettings.changeEmail')}</span>
                    <span className="md:hidden">Changer</span>
                  </Button>
                )}
              </div>
              
              {activeForm === 'email' && (
                <form onSubmit={emailForm.handleSubmit(onUpdateEmail)} className="space-y-3 ml-6 mt-3">
                  <div className="space-y-2">
                    <Label htmlFor="current_email" className="text-sm font-medium">
                      {t('accountSettings.currentEmail')}
                    </Label>
                    <Input
                      id="current_email"
                      value={user?.email || ''}
                      disabled
                      className="bg-gray-50 text-gray-500"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newEmail" className="text-sm font-medium">
                      {t('accountSettings.newEmail')} *
                    </Label>
                    <Input
                      id="newEmail"
                      type="email"
                      {...emailForm.register('newEmail')}
                    />
                    {emailForm.formState.errors.newEmail && (
                      <p className="text-sm text-red-600">
                        {emailForm.formState.errors.newEmail.message}
                      </p>
                    )}
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-blue-900">
                      <strong>{t('accountSettings.important')}:</strong> {t('accountSettings.emailChangeNote')}
                    </p>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button 
                      type="submit" 
                      disabled={emailForm.formState.isSubmitting}
                      className="bg-orange-500 hover:bg-orange-600 text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 flex-1"
                    >
                      <span className="hidden md:inline">{emailForm.formState.isSubmitting ? t('accountSettings.updating') : t('accountSettings.updateEmail')}</span>
                      <span className="md:hidden">{emailForm.formState.isSubmitting ? 'Mise à jour...' : 'Valider'}</span>
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setActiveForm(null)}
                      className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4"
                    >
                      {t('accountSettings.cancel')}
                    </Button>
                  </div>
                </form>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100 my-4" />

            {/* Password Section */}
            <div>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <Lock className="h-4 w-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">{t('accountSettings.password')}</span>
                  </div>
                  {activeForm !== 'password' && (
                    <p className="text-sm text-gray-900 ml-6">••••••••</p>
                  )}
                </div>
                {activeForm !== 'password' && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setActiveForm('password')}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 -mt-1 text-[10px] md:text-xs flex-shrink-0 h-7 px-2"
                  >
                    <span className="hidden md:inline">{t('accountSettings.changePassword')}</span>
                    <span className="md:hidden">Changer</span>
                  </Button>
                )}
              </div>

              {activeForm === 'password' && (
                <form onSubmit={passwordForm.handleSubmit(onUpdatePassword)} className="space-y-3 ml-6 mt-3">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-sm font-medium">
                      {t('accountSettings.newPassword')} *
                    </Label>
                    <Input
                      id="newPassword"
                      type="password"
                      {...passwordForm.register('newPassword')}
                    />
                    {passwordForm.formState.errors.newPassword && (
                      <p className="text-sm text-red-600">
                        {passwordForm.formState.errors.newPassword.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium">
                      {t('accountSettings.confirmPassword')} *
                    </Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      {...passwordForm.register('confirmPassword')}
                    />
                    {passwordForm.formState.errors.confirmPassword && (
                      <p className="text-sm text-red-600">
                        {passwordForm.formState.errors.confirmPassword.message}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button 
                      type="submit" 
                      disabled={passwordForm.formState.isSubmitting}
                      className="bg-orange-500 hover:bg-orange-600 text-xs md:text-sm h-8 md:h-9 px-3 md:px-4 flex-1"
                    >
                      <span className="hidden md:inline">{passwordForm.formState.isSubmitting ? t('accountSettings.updating') : t('accountSettings.updatePassword')}</span>
                      <span className="md:hidden">{passwordForm.formState.isSubmitting ? 'Mise à jour...' : 'Valider'}</span>
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setActiveForm(null)}
                      className="text-xs md:text-sm h-8 md:h-9 px-3 md:px-4"
                    >
                      {t('accountSettings.cancel')}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* MFA Section */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-violet-50 rounded-lg flex-shrink-0">
              <Shield className="h-5 w-5 text-violet-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm md:text-base">Authentification à deux facteurs (MFA)</h3>
              <p className="text-xs text-gray-500">Protégez votre compte avec Google/Microsoft Authenticator</p>
            </div>
            {!mfaLoading && (
              mfaFactors.length > 0
                ? <Badge className="bg-green-100 text-green-700 border-green-200 flex-shrink-0"><ShieldCheck className="h-3 w-3 mr-1" />Actif</Badge>
                : <Badge variant="outline" className="text-gray-500 flex-shrink-0"><ShieldOff className="h-3 w-3 mr-1" />Inactif</Badge>
            )}
          </div>

          <div className="space-y-4 pl-0 md:pl-11">
            {mfaLoading ? (
              <div className="h-8 bg-muted animate-pulse rounded" />
            ) : mfaFactors.length === 0 ? (
              <div className="space-y-3">
                {requiresMFA() && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    <Shield className="h-4 w-4 flex-shrink-0" />
                    Le MFA est obligatoire pour votre compte. Configurez-le pour accéder à toutes les fonctionnalités.
                  </div>
                )}
                <Button
                  onClick={() => navigate('/mfa?mode=enroll')}
                  className="gap-2 bg-violet-600 hover:bg-violet-700"
                  size="sm"
                >
                  <Smartphone className="h-4 w-4" />
                  Configurer le MFA
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {mfaFactors.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Authenticator App</p>
                        <p className="text-xs text-green-600">Configuré le {new Date(f.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                    {!requiresMFA() && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs"
                        disabled={unenrolling}
                        onClick={() => handleUnenroll(f.id)}
                      >
                        <ShieldOff className="h-3.5 w-3.5 mr-1" />
                        Désactiver
                      </Button>
                    )}
                  </div>
                ))}

                <Separator />

                {/* Appareils de confiance */}
                <TrustedDevicesManager />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preferences Section */}      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <Languages className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg">{t('accountSettings.profileSettings')}</h3>
              <p className="text-sm text-gray-500">{t('accountSettings.customizeExperience')}</p>
            </div>
          </div>

          <div className="space-y-4 pl-0 md:pl-11">
            {/* Language Section */}
            <div className="flex justify-between items-center py-2">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('accountSettings.language')}</span>
                <p className="text-xs text-gray-500 mt-0.5">Choose your preferred language</p>
              </div>
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue>
                    <span className="text-sm">
                      {language === 'fr' && '🇫🇷 Français'}
                      {language === 'en' && '🇬🇧 English'}  
                      {language === 'lg-fr' && '🇨🇩 Lingala'}
                      {language === 'sw' && '🇹🇿 Swahili'}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr">🇫🇷 Français</SelectItem>
                  <SelectItem value="en">🇬🇧 English</SelectItem>
                  <SelectItem value="lg-fr">🇨🇩 Lingala</SelectItem>
                  <SelectItem value="sw">🇹🇿 Swahili</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-gray-100" />

            {/* Currency Section */}
            <div className="flex justify-between items-center py-2">
              <div>
                <span className="text-sm font-medium text-gray-700">{t('accountSettings.currency')}</span>
                <p className="text-xs text-gray-500 mt-0.5">Select your display currency</p>
              </div>
              <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue>
                    <span className="text-sm">
                      {displayCurrency === 'CDF' && 'FC - Congolais'}
                      {displayCurrency === 'USD' && '$ - Dollar US'}
                      {displayCurrency === 'EUR' && '€ - Euro'}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CDF">FC - Franc Congolais</SelectItem>
                  <SelectItem value="USD">$ - Dollar US</SelectItem>
                  <SelectItem value="EUR">€ - Euro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountSettings;